<template>
  <div class="flex flex-wrap box justify-between gap-y-3 gap-x-8 xl:gap-x-12">

    <div class="min-w-0">
      <div v-if="isAdmin" class="flex mb-2">
        <jet-label for="customer" value="Customer:" class="w-32 xl:w-20 align-top leading-7" />
        <div class="flex flex-1 flex-col">
          <multiselect
            class="multi-lg h-7 text-base"
            v-model="form.customer_id"
            :options="customers"
            valueProp="id"
            trackBy="name"
            label="name"
            :searchable="true"
            placeholder="Select Customer"
          />
          <jet-input-error :message="$page.props.errors.customer_id" class="m-1" />
        </div>
      </div>

      <div v-else>
        <div class="flex mb-2">
          <jet-label value="Customer:" class="w-32 xl:w-20 align-top leading-7" />
          <div class="flex flex-1 flex-col">
            <div>{{ activeCustomer?.name || 'No active customer' }}</div>
          </div>
        </div>
      </div>

      <template v-if="selectedCustomer">
        <div class="flex mb-2">
          <jet-label for="address" value="Address:" class="w-32 xl:w-20 align-top leading-7" />
          <div class="flex flex-1 flex-col">
            <div>{{ selectedCustomer.address_1 }}</div>
            <div>{{ selectedCustomer.address_2 }}</div>
            <div>{{ selectedCustomer.city }} {{ selectedCustomer.province }}, {{ selectedCustomer.postal_code }}</div>
          </div>
        </div>

        <div class="flex mb-2">
          <jet-label for="phone" value="Phone:" class="w-32 xl:w-20 align-top leading-7" />
          <div>{{ selectedCustomer.phone }}</div>
        </div>

        <div class="flex items-start" v-if="selectedCustomer.emails?.length">
          <jet-label for="email" value="Email:" class="w-32 xl:w-20 align-top leading-7" />
          <ul class="leading-tight">
            <li v-for="email in selectedCustomer.emails" :key="email">{{ email }}</li>
          </ul>
        </div>

        <div class="flex flex-wrap items-start gap-x-3 gap-y-1 mb-2">
          <jet-label
            for="shipping_method"
            value="Shipping Method:"
            class="shrink-0 w-36 max-w-full align-top leading-snug break-words sm:pt-0.5"
          />
          <div class="min-w-0 w-full flex-1 sm:min-w-[12rem]">
            <multiselect
              class="multi-lg h-7 text-base w-full min-w-0"
              v-model="form.shipping_method"
              :options="shippingOptions"
              valueProp="value"
              trackBy="value"
              label="label"
              :canClear="false"
              :canDeselect="false"
            />
            <jet-input-error :message="$page.props.errors.shipping_method" class="m-1" />
          </div>
        </div>
      </template>
    </div>

    <div class="flex flex-1 flex-col">
      <div class="flex mb-2">
        <jet-label for="schedule_days" value="Days of Week:" class="w-32 align-top leading-7" />
        <div class="flex-1">
          <multiselect
            class="multi-lg h-7 text-base"
            v-model="form.schedule_days"
            :options="daysOfWeek"
            label="label"
            valueProp="value"
            mode="multiple"
            :closeOnSelect="false"
            placeholder="Select days"
            :classes="{multipleLabel: 'w-full px-2'}"
          >
            <template v-slot:multiplelabel="{ values }">
              <div class="multiselect-multiple-label">
                {{ values.length }} day{{ values.length > 1 ? 's' : ''}}
              </div>
            </template>
          </multiselect>
          <div v-show="form.schedule_days.length" class="flex flex-wrap gap-2 mt-1">
            <Pill v-for="day in form.schedule_days" :key="day" color="primary-green" size="xs">
              {{ daysOfWeek.find(d => d.value === day).label }}
            </Pill>
          </div>
          <jet-input-error :message="$page.props.errors.schedule_days" class="m-1" />
        </div>
      </div>

      <div class="flex mb-2">
        <jet-label for="schedule_frequency" value="Repeat every:" class="w-32 align-top leading-7" />
        <div class="flex-1">
          <InputGroup>
              <jet-input
                v-model.number="form.schedule_frequency"
                type="number"
                min="1"
                class="w-16 text-right rounded-r-none"
              />
              <div class="justify-start">
                <multiselect
                  class="multi-lg text-sm rounded-l-none w-full h-[34px]"
                  style="width: 140px;"
                  v-model="form.schedule_units"
                  :options="scheduleUnits"
                  label="label"
                  valueProp="value"
                  :closeOnSelect="true"
                  :canClear="false"
                />
              </div>
          </InputGroup>
          <jet-input-error :message="$page.props.errors.schedule_frequency" class="m-1" />
        </div>
      </div>

      <div v-show="form.schedule_units == 'months'" class="flex mb-2">
        <jet-label for="schedule_ordinance" value="Which days:" class="w-32 align-top leading-7" />
        <div class="flex-1">
          <multiselect
            class="multi-lg h-7 text-base"
            v-model="form.schedule_ordinance"
            :options="scheduleOrdinance"
            label="label"
            valueProp="value"
            mode="multiple"
            :closeOnSelect="false"
            placeholder="First, Second, etc."
            :classes="{multipleLabel: 'w-full px-2'}"
          >
            <template v-slot:multiplelabel="{ values }">
              <div class="multiselect-multiple-label">
                <span v-for="(val, index) in form.schedule_ordinance" :key="val">
                {{ scheduleOrdinance.find(v => v.value === val).label }}<span v-if="index < form.schedule_ordinance.length - 1">,&nbsp;</span>
                </span>
              </div>
            </template>
          </multiselect>

          <!-- Conditionally render text based on schedule_days and schedule_ordinance length,
          i.e. "Future orders will be placed on the first, second and third Mondays and Thursdays of the month".
          Commas and pluralization handled appropriately. -->
          <div v-if="form.schedule_ordinance?.length">
            <p v-if="form.schedule_days.length" class="text-xs text-gray-500 mt-1">
              Future orders will be placed on the
              <span v-for="(val, index) in form.schedule_ordinance" :key="val">
                {{ scheduleOrdinance.find(v => v.value === val).value }}<span v-if="index < form.schedule_ordinance.length - 2">, </span>
                <span v-else-if="form.schedule_ordinance.length > 1 && index === form.schedule_ordinance.length - 2"> and </span>
              </span>
              {{ form.schedule_ordinance.length === 1 ? '' : ' ' }}
              <span v-for="(val, index) in form.schedule_days" :key="val">
                 {{ daysOfWeek.find(v => v.value === val).label }}{{ form.schedule_ordinance.length > 1 ? 's' : '' }}<span v-if="index < form.schedule_days.length - 2">, </span>
                <span v-else-if="index === form.schedule_days.length - 2"> and </span>
              </span>
              of the month.
            </p>
            <p v-else class="text-xs text-gray-500 mt-1">
              Please select one or more days of the week to place the order on.
            </p>
          </div>
          
          <jet-input-error :message="$page.props.errors.schedule_ordinance" class="m-1" />
        </div>
      </div>

      <div class="flex mb-2">
        <jet-label for="weeks_in_advance" value="Place order:" class="w-32 align-top leading-7" />
        <div>
          <InputGroup>
              <jet-input
                v-model.number="form.weeks_in_advance"
                type="number"
                min="1"
                class="w-16 text-right rounded-r-none"
              />
              <InputGroupText style="width: 140px;" position="append"> 
                Week{{ form.weeks_in_advance > 1 ? 's' : '' }} in Advance
              </InputGroupText>
          </InputGroup>
          <jet-input-error :message="$page.props.errors.weeks_in_advance" class="m-1" />
          <p class="text-xs text-gray-500 mt-1">
            Future orders will be placed {{ form.weeks_in_advance }} week{{ form.weeks_in_advance > 1 ? 's' : '' }} in advance.
          </p>
        </div>
      </div>

      <div class="flex mb-2">
        <jet-label
          for="next_generation_date"
          value="Next Order Date:"
          class="w-32 align-top leading-7 whitespace-nowrap"
        />

        <div class="flex flex-1 flex-col">
          <datepicker
            v-model="nextGenerationDate"
            class="w-100"
            inputClassName="dp-custom-input"
            weekStart="0"
            monthNameFormat="long"
            :enableTimePicker="false"
            :autoApply="true"
            :is24="false"
            :clearable="false"
            :format="formatDate"
            :minDate="minDate"
            :maxDate="maxDate"
            :disabled-dates="disabledDates"
            position="right"
          />
          <p class="text-xs text-gray-500 mt-1">
            Selecting a date here will override the automatic generation schedule.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import Pill from '@/Jetstream/Pill.vue';
import InputGroup from '@/Jetstream/InputGroup.vue';
import InputGroupText from '@/Jetstream/InputGroupText.vue';
import '@vueform/multiselect/themes/default.css';
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { DateTime } from 'luxon'
import { computed, watch, ref } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth'

const props = defineProps({
  customers: Array,
  form: Object,
  errors: Object,
  isAdmin: Boolean,
  activeCustomer: Object
});

const { isAdmin } = UseUserAuth(usePage().props.value.user)

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
];

const daysOfWeekOrder = daysOfWeek.reduce((map, item, index) => {
  map[item.value] = index;
  return map;
}, {});

const scheduleUnits = computed(() => {
  const frequency = props.form.schedule_frequency || 1;
  const isPlural = frequency !== 1;
  return [
    { label: `Week${isPlural ? 's' : ''}`, value: 'weeks' },
    { label: `Month${isPlural ? 's' : ''}`, value: 'months' },
  ];
});

const shippingOptions = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'pickup', label: 'Pickup' },
];

const scheduleOrdinance = [
  { label: 'First', value: 'first' },
  { label: 'Second', value: 'second' },
  { label: 'Third', value: 'third' },
  { label: 'Fourth', value: 'fourth' },
  { label: 'Last', value: 'last' },
];

const ordinanceOrder = scheduleOrdinance.reduce((map, item, index) => {
  map[item.value] = index;
  return map;
}, {});

const selectedCustomer = computed(() => {
  if (props.form.customer_id && props.customers?.length) {
    return props.customers.find(c => c.id === props.form.customer_id) || null
  }
  if (!props.isAdmin && props.activeCustomer) {
    return props.activeCustomer
  }
  return null
})

const weekdayMap = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

const nextGenerationDate = ref(
  props.form.next_generation_date
    ? DateTime.fromISO(props.form.next_generation_date).toJSDate()
    : null
)

// When user manually picks a date
watch(nextGenerationDate, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    props.form.next_generation_date = DateTime.fromJSDate(newVal).toISODate()
  }
})

let initialized = false

// Calculate generation day when schedule_days or weeks_in_advance change
watch(
  () => [props.form.schedule_days, props.form.weeks_in_advance],
  ([days, weeks]) => {
    // Skip the first run if next_generation_date already exists
    if (!initialized) {
      initialized = true
      if (props.form.next_generation_date) return
    }

    const autoDate = calculateNextGenerationDate()
    if (autoDate) {
      nextGenerationDate.value = autoDate
      props.form.next_generation_date = DateTime.fromJSDate(autoDate).toISODate()
    }
  },
  { immediate: true }
)

// Sort ordinance on selection
watch(
  () => props.form.schedule_ordinance,
  (val) => {
    const sorted = [...val].sort((a, b) => ordinanceOrder[a] - ordinanceOrder[b]);
    if (sorted.some((v, i) => v !== val[i])) {
      props.form.schedule_ordinance = sorted;
    }
  },
  { deep: false }
);

// Sort days of week on selection
watch(
  () => props.form.schedule_days,
  (val) => {
    const sorted = [...val].sort((a, b) => daysOfWeekOrder[a] - daysOfWeekOrder[b]);
    if (sorted.some((v, i) => v !== val[i])) {
      props.form.schedule_days = sorted;
    }
  },
  { deep: false }
);

watch(
  () => props.activeCustomer, (newVal) => {
    if (!props.isAdmin && newVal && props.form.customer_id !== newVal.id) {
      props.form.customer_id = newVal.id;
    }
  }, { immediate: true }
);

// When the selected customer changes, default shipping to that customer's preference (same idea as Orders CustomerForm).
watch(
  selectedCustomer,
  (newVal, oldVal) => {
    if (newVal == null) {
      props.form.shipping_method = 'delivery';
      return;
    }
    const isSameCustomer = oldVal != null && oldVal.id === newVal.id;
    if (oldVal == null || !isSameCustomer) {
      props.form.shipping_method = newVal.default_shipping_method || 'delivery';
    }
  }
);

function calculateNextGenerationDate() {
  if (!props.form.schedule_days?.length || !props.form.schedule_frequency || !props.form.weeks_in_advance) {
    return null
  }

  const today = DateTime.now()
  const selectedDays = props.form.schedule_days
  const nextDates = selectedDays.map(day => {
    const targetWeekday = weekdayMap[day]
    let nextDay = today.plus({ days: (targetWeekday - today.weekday + 7) % 7 })
    if (nextDay <= today) nextDay = nextDay.plus({ weeks: 1 })
    return nextDay
  })

  const nextBaseDate = nextDates.reduce((a, b) => (a < b ? a : b))
  return nextBaseDate.toJSDate()
}

const minDate = computed(() => {
  return DateTime.now().plus({ days: 3 }).startOf('day').toJSDate()
})

const maxDate = computed(() => {
  return DateTime.now().plus({ months: 12 }).endOf('day').toJSDate()
})

const disabledDates = computed(() => {
  const selectedDays = props.form.schedule_days || []
  if (!selectedDays.length) return []

  const today = DateTime.now().startOf('day')
  const end = today.plus({ months: 12 })
  const disabled = []

  // If monthly schedule with ordinance, calculate valid dates based on ordinance pattern
  if (props.form.schedule_units === 'months' && props.form.schedule_ordinance?.length) {
    const allowedWeekdays = selectedDays.map(d => weekdayMap[d])
    const ordinances = props.form.schedule_ordinance || []
    
    // Get all valid dates for the next 12 months
    const validDates = new Set()
    
    let currentMonth = today.startOf('month')
    while (currentMonth <= end) {
      const year = currentMonth.year
      const month = currentMonth.month
      
      // Get all valid dates for this month based on ordinance
      const monthValidDates = getMonthlyValidDates(year, month, allowedWeekdays, ordinances)
      monthValidDates.forEach(date => {
        const normalizedDate = date.startOf('day')
        if (normalizedDate >= today && normalizedDate <= end) {
          validDates.add(normalizedDate.toISODate())
        }
      })
      
      currentMonth = currentMonth.plus({ months: 1 })
    }
    
    // Disable all dates that are not in the valid dates set
    let x = today.startOf('day')
    while (x <= end) {
      if (!validDates.has(x.toISODate())) {
        disabled.push(new Date(x.toJSDate()))
      }
      x = x.plus({ days: 1 })
    }
  } else {
    // For weekly schedules, just disable dates that don't match allowed weekdays
    const allowedWeekdays = selectedDays.map(d => weekdayMap[d])
    let x = today.startOf('day')
    while (x <= end) {
      if (!allowedWeekdays.includes(x.weekday)) {
        disabled.push(new Date(x.toJSDate()))
      }
      x = x.plus({ days: 1 })
    }
  }

  return disabled
})

// Calculate valid dates for a given month based on ordinance pattern
function getMonthlyValidDates(year, month, weekdays, ordinances) {
  const validDates = []
  
  ordinances.forEach(ordinance => {
    weekdays.forEach(weekday => {
      if (ordinance === 'last') {
        // Find the last occurrence of this weekday in the month
        let date = DateTime.fromObject({ year, month, day: 1 }).endOf('month').startOf('day')
        while (date.weekday !== weekday) {
          date = date.minus({ days: 1 })
        }
        validDates.push(date.startOf('day'))
      } else {
        // Find the nth occurrence (first, second, third, fourth)
        const ordinanceMap = {
          'first': 1,
          'second': 2,
          'third': 3,
          'fourth': 4
        }
        const occurrence = ordinanceMap[ordinance]
        
        if (occurrence) {
          // Start from first of month
          let date = DateTime.fromObject({ year, month, day: 1 }).startOf('day')
          
          // Move to the first occurrence of the given weekday
          while (date.weekday !== weekday) {
            date = date.plus({ days: 1 })
          }
          
          // Find the nth occurrence
          date = date.plus({ weeks: occurrence - 1 })
          
          // Ensure still same month
          if (date.month === month) {
            validDates.push(date.startOf('day'))
          }
        }
      }
    })
  })
  
  return validDates
}

const formatDate = (date) => {
  return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd')
}

</script>

<style scoped>
.box > * {
  flex: 1 1 300px;
}
</style>